import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Autocomplete, {
  autocompleteClasses,
  createFilterOptions,
  type AutocompleteRenderGroupParams,
} from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { inputBaseClasses } from "@mui/material/InputBase";
import InputAdornment from "@mui/material/InputAdornment";
import type { PopperProps } from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { useEffect, useState, type ReactNode } from "react";

import type { NavSearchEntry } from "../../navigation";
import { SHELL_BACKDROP, SHELL_SURFACE, shellColors, type ShellAppearance } from "./tokens";

export interface ModeSearchDialogProps {
  open: boolean;
  entries: readonly NavSearchEntry[];
  onClose: () => void;
  onSelect: (entry: NavSearchEntry) => void;
  /** Business or platform: the scrim tone and the selected-row tint. */
  appearance?: ShellAppearance;
}

const filterEntries = createFilterOptions<NavSearchEntry>({
  stringify: (entry) =>
    [entry.label, entry.modeLabel, entry.parentLabel ?? "", ...entry.keywords].join(" "),
});

/** Where the entry lives, as the menu names it: "Setup › Pricing › Drop-in". */
export function searchBreadcrumb(entry: NavSearchEntry): string {
  return [entry.modeLabel, entry.parentLabel, entry.label]
    .filter((part): part is string => Boolean(part))
    .join(" › ");
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        px: 0.625,
        border: 1,
        borderRadius: "4px",
        fontFamily: "inherit",
        fontSize: 10.5,
        lineHeight: 1.3,
        ...shellColors(theme, {
          borderColor: "line",
          color: "muted",
          backgroundColor: "surface",
        }),
      })}
    >
      {children}
    </Box>
  );
}

/** The results render inline under the field, like the mockup's ⌘K palette. */
function InlinePopper({ open, children }: PopperProps) {
  if (!open) return null;
  return <Box>{children as ReactNode}</Box>;
}

function InlinePaper({ children }: { children?: ReactNode }) {
  return <Box>{children}</Box>;
}

function renderGroup(params: AutocompleteRenderGroupParams) {
  return (
    <Box component="li" key={params.key} sx={{ listStyle: "none" }}>
      <Typography
        component="div"
        sx={(theme) => ({
          px: "10px",
          pt: 1,
          pb: "3px",
          fontSize: 11.5,
          fontWeight: 600,
          ...shellColors(theme, { color: "muted" }),
        })}
      >
        {params.group}
      </Typography>
      <Box component="ul" sx={{ p: 0, m: 0 }}>
        {params.children}
      </Box>
    </Box>
  );
}

const listboxSx = (appearance: ShellAppearance) => (theme: Theme) => ({
  maxHeight: "min(420px, 60vh)",
  p: "6px",
  [`& .${autocompleteClasses.option}`]: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: 34,
    height: 34,
    px: "10px",
    py: 0,
    borderRadius: "6px",
    fontSize: 13,
    ...shellColors(theme, { color: "ink2" }),
  },
  [`& .${autocompleteClasses.option}.Mui-focused, & .${autocompleteClasses.option}[aria-selected="true"], & .${autocompleteClasses.option}[aria-selected="true"].Mui-focused`]:
    // Business selects in slate (mockup `.menu-i.on`), platform in emerald (`.pal-r.on`).
    shellColors(theme, {
      backgroundColor: appearance === "platform" ? "activeTint" : "fill",
      color: "ink",
    }),
});

/** ⌘K: one box that finds every page by its menu name, mode or keyword. */
export function ModeSearchDialog({
  open,
  entries,
  onClose,
  onSelect,
  appearance = "business",
}: ModeSearchDialogProps) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        backdrop: { sx: { bgcolor: SHELL_BACKDROP[appearance] } },
        paper: {
          "aria-label": "Search",
          sx: (theme: Theme) => ({
            alignSelf: "flex-start",
            m: 0,
            mt: { xs: 2, sm: "88px" },
            width: "min(640px, calc(100% - 32px))",
            maxWidth: "none",
            borderRadius: "10px",
            border: 1,
            boxShadow: SHELL_SURFACE.popShadow,
            overflow: "hidden",
            backgroundImage: "none",
            ...shellColors(theme, { borderColor: "line", backgroundColor: "surface" }),
          }),
        },
      }}
    >
      <Autocomplete<NavSearchEntry, false, true, false>
        open={open}
        autoHighlight
        disableClearable
        forcePopupIcon={false}
        options={[...entries]}
        value={undefined}
        inputValue={query}
        groupBy={() => "Pages"}
        renderGroup={renderGroup}
        getOptionLabel={(entry) => entry.label}
        // Labels repeat across modes (Programs page, Reports › Programs).
        getOptionKey={(entry) => entry.pageId}
        isOptionEqualToValue={(option, value) => option.pageId === value.pageId}
        filterOptions={filterEntries}
        onInputChange={(_event, value, reason) => {
          if (reason === "input" || reason === "clear") setQuery(value);
        }}
        onChange={(_event, entry) => {
          if (entry) onSelect(entry);
        }}
        // The listbox is always open, so Autocomplete consumes Escape before the
        // Dialog sees it; close the dialog from here (focus returns to the trigger).
        onClose={(_event, reason) => {
          if (reason === "escape") onClose();
        }}
        noOptionsText="No pages match."
        slots={{ popper: InlinePopper, paper: InlinePaper }}
        slotProps={{ listbox: { sx: listboxSx(appearance) } }}
        renderOption={(props, entry) => {
          const { key, ...optionProps } = props as typeof props & { key: string };
          const EntryIcon = entry.icon;
          return (
            <Box component="li" key={key} {...optionProps}>
              <Box
                component="span"
                sx={{ flexShrink: 0, whiteSpace: "nowrap", fontSize: 13, fontWeight: 400 }}
              >
                {entry.label}
              </Box>
              <Box
                component="span"
                sx={(theme) => ({
                  ml: "auto",
                  pl: 1.5,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 11.5,
                  whiteSpace: "nowrap",
                  ...shellColors(theme, { color: "muted2" }),
                })}
              >
                {searchBreadcrumb(entry)}
              </Box>
              <Box
                component="span"
                aria-hidden="true"
                sx={(theme) => ({
                  // Drawn first; kept last in the DOM so the label leads the row.
                  order: -1,
                  display: "inline-flex",
                  flexShrink: 0,
                  fontSize: 18,
                  ...shellColors(theme, { color: "muted" }),
                })}
              >
                <EntryIcon fontSize="inherit" />
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            fullWidth
            variant="standard"
            placeholder="Search or jump to"
            slotProps={{
              ...params.slotProps,
              htmlInput: { ...params.slotProps.htmlInput, "aria-label": "Search" },
              input: {
                ...params.slotProps.input,
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: "10px" }}>
                    <SearchOutlinedIcon
                      aria-hidden="true"
                      sx={(theme) => ({ fontSize: 20, ...shellColors(theme, { color: "muted" }) })}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Kbd>esc</Kbd>
                  </InputAdornment>
                ),
              },
            }}
            sx={(theme) => ({
              // A plain 52px row (mockup `.pal-in`). Focus shows as a 2px emerald
              // bottom edge in place of the theme's ring around the field.
              justifyContent: "center",
              height: 52,
              px: 2,
              borderBottom: 1,
              ...shellColors(theme, { borderColor: "line" }),
              "&:focus-within": {
                boxShadow: `inset 0 -1px 0 ${SHELL_SURFACE.focus}`,
                ...shellColors(theme, { borderColor: "focus" }),
              },
              [`& .${inputBaseClasses.root}`]: {
                minHeight: 0,
                fontSize: 15,
                ...shellColors(theme, { color: "ink" }),
              },
              [`& .${inputBaseClasses.root}.${inputBaseClasses.focused}`]: {
                outline: "none",
              },
              [`& .${inputBaseClasses.input}`]: { py: 0, height: 24 },
            })}
          />
        )}
      />
      <Box
        aria-hidden="true"
        sx={(theme) => ({
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          gap: 1.75,
          height: 34,
          px: 1.75,
          borderTop: 1,
          fontSize: 11.5,
          ...shellColors(theme, { borderColor: "line", color: "muted" }),
        })}
      >
        <span>
          <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
        </span>
        <span>
          <Kbd>↵</Kbd> open
        </span>
        <Box component="span" sx={{ ml: "auto" }}>
          <Kbd>esc</Kbd> close
        </Box>
      </Box>
    </Dialog>
  );
}
